import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { Layout } from '@/components/layout/Layout';
import { EnhancedRegisterPage } from '@/pages/auth/EnhancedRegisterPage';
import { EmailConfirmationPage } from '@/pages/auth/EmailConfirmationPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { PublicLandingPage } from '@/pages/PublicLandingPage';
import { PricingPage } from '@/pages/PricingPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { ChangePasswordPage } from '@/pages/auth/ChangePasswordPage';
import { ReceivedInvitationsPage } from '@/pages/partner/ReceivedInvitationsPage';
import { PePortfolioPage } from '@/pages/pe-portfolio/PePortfolioPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { TransformationsPage } from '@/pages/transformations/TransformationsPage';
import { TransformationFormPage } from '@/pages/transformations/TransformationFormPage';
import { TransformationDetailPage } from '@/pages/transformations/TransformationDetailPage';
import { KPIsPage } from '@/pages/kpis/KPIsPage';
import { KPIFormPage } from '@/pages/kpis/KPIFormPage';
import { KPIDetailPage } from '@/pages/kpis/KPIDetailPage';
import { TenantsPage } from '@/pages/tenants/TenantsPage';
import { TenantFormPage } from '@/pages/tenants/TenantFormPage';
import { TenantDetailPage } from '@/pages/tenants/TenantDetailPage';
import { ProcessModelsPage } from '@/pages/process-models/ProcessModelsPage';
import { ChangeManagementPage } from '@/pages/change-management/ChangeManagementPage';
import { DataImportPage } from '@/pages/data-import/DataImportPage';
import { RiskManagementPage } from '@/pages/risk/RiskManagementPage';
import { BenchmarkingPage } from '@/pages/benchmarking/BenchmarkingPage';
import { BenchmarkEntriesPage } from '@/pages/benchmarking/BenchmarkEntriesPage';
import { HierarchyPropagationPage } from '@/pages/benchmarking/HierarchyPropagationPage';
import { BenchmarkProcessAdminPage } from '@/pages/benchmarking/BenchmarkProcessAdminPage';
import { ResourcesPage } from '@/pages/resources/ResourcesPage';
import TemplateManagementPage from '@/pages/templates/TemplateManagementPage';
import TemplateHierarchyEditorPage from '@/pages/templates/TemplateHierarchyEditorPage';
import HierarchyTemplatesPage from '@/pages/templates/HierarchyTemplatesPage';
import RoadmapTemplatesPage from '@/pages/templates/RoadmapTemplatesPage';
import RoleTemplatesPage from '@/pages/templates/RoleTemplatesPage';
import ProjectStructureTemplatesPage from '@/pages/templates/ProjectStructureTemplatesPage';
import { PureHierarchicalTemplateBuilder } from '@/pages/templates/PureHierarchicalTemplateBuilder';
import { ProjectStructureTemplateBuilder } from '@/pages/templates/ProjectStructureTemplateBuilder';
import { ResourceViewPage } from '@/pages/resources/ResourceViewPage';
import { ResourcesAdminPage } from '@/pages/resources/ResourcesAdminPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { UserProfilePage } from '@/pages/settings/UserProfilePage';
import TenantConfigurationPage from '@/pages/tenantconfigurationPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ProtectedRBACRoute } from '@/components/auth/ProtectedRBACRoute';
import { RoleManagementPage } from '@/pages/rbac/RoleManagementPage';
import { RoleEditPage } from '@/pages/rbac/RoleEditPage';
import { RoleCreatePage } from '@/pages/rbac/RoleCreatePage';

// Import all the new placeholder pages
import UserManagementPage from '@/pages/usermanagementPage';
import AccessManagementPage from '@/pages/access-management/AccessManagementPage';

// Import additional placeholder pages
import NavigationHierarchyPage from '@/pages/platform/NavigationHierarchyPage';
import NavigationManagementPage from '@/pages/platform/NavigationManagementPage';
import PlatformSettingsPage from '@/pages/platform/PlatformSettingsPage';
import AuditLoggingPage from '@/pages/auditloggingPage';
import SecurityManagementPage from '@/pages/securitymanagementPage';
import BackupRecoveryPage from '@/pages/backuprecoveryPage';
import SystemMonitoringPage from '@/pages/systemmonitoringPage';
import PerformanceTuningPage from '@/pages/performancetuningPage';
import IntegrationManagementPage from '@/pages/integrationmanagementPage';
import ApiManagementPage from '@/pages/apimanagementPage';
import LicenseManagementPage from '@/pages/licensemanagementPage';


// ABAC Management Pages
import CustomAttributesPage from '@/pages/abac/CustomAttributesPage';
import UserAttributesPage from '@/pages/abac/UserAttributesPage';
import ResourceAttributesPage from '@/pages/abac/ResourceAttributesPage';
import EnvironmentAttributesPage from '@/pages/abac/EnvironmentAttributesPage';
import TestEnvironmentPage from '@/pages/abac/TestEnvironmentPage';
import ActionsPage from '@/pages/abac/ActionsPage';
import ViewUsersPage from '@/pages/abac/ViewUsersPage';
import ViewResourcesPage from '@/pages/abac/ViewResourcesPage';
import UserAttributeAssignmentPage from '@/pages/abac/UserAttributeAssignmentPage';
import ResourceAttributeAssignmentPage from '@/pages/abac/ResourceAttributeAssignmentPage';
import TestUserAttributePage from '@/pages/abac/TestUserAttributePage';
import { PolicyManagementPage } from '@/pages/abac/PolicyManagementPage';
import { PolicyCreatePage } from '@/pages/abac/PolicyCreatePage';
import { PolicyTestPage } from '@/pages/abac/PolicyTestPage';
import AcceptInvitationPage from '@/pages/partner/AcceptInvitationPage';
import PartnerDirectoryPage from '@/pages/partner/PartnerDirectoryPage';
import PasswordManagementPage from '@/pages/admin/PasswordManagementPage';
import { UserApprovalPage } from '@/pages/admin/UserApprovalPage';
import { UserPositionManagementPage } from '@/pages/admin/UserPositionManagementPage';
import { PositionTemplatesPage } from '@/pages/admin/PositionTemplatesPage';
import { TenantPositionsPage } from '@/pages/admin/TenantPositionsPage';
import { ProvisioningProfilesPage } from '@/pages/admin/ProvisioningProfilesPage';
import UserProvisioningPage from '@/pages/admin/UserProvisioningPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { ModulePage } from '@/pages/modules/ModulePage';

// Roadmap Marketplace Pages
import { RoadmapMarketplacePage } from '@/pages/roadmap-marketplace/RoadmapMarketplacePage';
import { RoadmapDetailPage } from '@/pages/roadmap-marketplace/RoadmapDetailPage';
import { RoadmapsPage } from '@/pages/roadmap-marketplace/RoadmapsPage';
import { PlanningPage } from '@/pages/ai/PlanningPage';
import { PlanningLegacyRedirect } from '@/pages/ai/PlanningLegacyRedirect';


// Alignment Management Pages
import AlignmentPage from '@/pages/AlignmentPage';

// AI-Powered Features Pages
import { AIPage } from '@/pages/ai/AIPage';
import { AISettingsPage } from '@/pages/ai/AISettingsPage';
import AIStudioHomePage from '@/pages/ai-studio/AIStudioHomePage';
import AIStudioConversationPage from '@/pages/ai-studio/AIStudioConversationPage';
import AIStudioAgentCatalogPage from '@/pages/ai-studio/AIStudioAgentCatalogPage';
import AIStudioAgentMarketplacePage from '@/pages/ai-studio/AIStudioAgentMarketplacePage';
import AIStudioAgentDetailPage from '@/pages/ai-studio/AIStudioAgentDetailPage';
import AIStudioAgentDefinitionPage from '@/pages/ai-studio/AIStudioAgentDefinitionPage';
import AIStudioAgentDefinitionRedirect from '@/pages/ai-studio/AIStudioAgentDefinitionRedirect';
import AIStudioVersionEditorPage from '@/pages/ai-studio/AIStudioVersionEditorPage';
import AIStudioPackCatalogPage from '@/pages/ai-studio/AIStudioPackCatalogPage';
import AIStudioPackEditorPage from '@/pages/ai-studio/AIStudioPackEditorPage';
import AIStudioInstanceCatalogPage from '@/pages/ai-studio/AIStudioInstanceCatalogPage';
import AIStudioProvisionInstancePage from '@/pages/ai-studio/AIStudioProvisionInstancePage';
import AIStudioDeployInstancePage from '@/pages/ai-studio/AIStudioDeployInstancePage';
import AIStudioInstanceEntryPage from '@/pages/ai-studio/AIStudioInstanceEntryPage';
import AIStudioInstanceSetupPage from '@/pages/ai-studio/AIStudioInstanceSetupPage';
import AIStudioBindingsRedirect from '@/pages/ai-studio/AIStudioBindingsRedirect';
import AIStudioRunCatalogPage from '@/pages/ai-studio/AIStudioRunCatalogPage';
import AIStudioHistoryHubPage from '@/pages/ai-studio/AIStudioHistoryHubPage';
import AIStudioRunWorkspacePage from '@/pages/ai-studio/AIStudioRunWorkspacePage';
import AIStudioOntologyBrowserPage from '@/pages/ai-studio/AIStudioOntologyBrowserPage';

// Data Connection Pages
import { 
  DataSourceManagerPage,
  DataSourceFormPage
} from '@/pages/data-connection';

// Hierarchy Management Pages
import HierarchiesPage from '@/pages/hierarchiesPage';
import BusinessHierarchyPage from '@/pages/businesshierarchyPage';
import FunctionalHierarchyPage from '@/pages/functionalhierarchyPage';
import GeographicHierarchyPage from '@/pages/geographichierarchyPage';

// CRM Pages
import { UnifiedCRMPage } from '@/pages/crm/UnifiedCRMPage';
import { NavigationTestPage } from '@/pages/navigation/NavigationTestPage';
import { OnboardingWizard } from '@/pages/onboarding/OnboardingWizard';
import { UnsubscribePage } from '@/pages/crm/UnsubscribePage';
// Public Website Pages
import { ProductPage } from '@/pages/public/ProductPage';
import { SolutionsPage } from '@/pages/public/SolutionsPage';
import { PrivacyPolicyPage } from '@/pages/public/PrivacyPolicyPage';
import { TermsOfServicePage } from '@/pages/public/TermsOfServicePage';
import { RequestDemoPage } from '@/pages/public/RequestDemoPage';
import { PublicPlanningPage } from '@/pages/public/PublicPlanningPage';
import { TakeAssessmentPage } from '@/pages/assessment-engine/TakeAssessmentPage';
import { CampaignListPage } from '@/pages/assessment-engine/CampaignListPage';
import { CampaignDeployWizard } from '@/pages/assessment-engine/campaigns/CampaignDeployWizard';
import { CampaignDetailHub } from '@/pages/assessment-engine/campaigns/CampaignDetailHub';
import { TemplateListPage } from '@/pages/assessment-engine/templates/TemplateListPage';
import { TemplateBuilderPage } from '@/pages/assessment-engine/templates/TemplateBuilderPage';
import { EngineSettingsPage } from '@/pages/assessment-engine/settings/EngineSettingsPage';
import { AssessmentReportPage } from '@/pages/assessment-engine/AssessmentReportPage';
import { AssessmentResultsPage } from '@/pages/assessment-engine/AssessmentResultsPage';
import { LiveReportPage } from '@/pages/assessment-engine/LiveReportPage';
import { InviteUnsubscribePage } from '@/pages/assessment-engine/InviteUnsubscribePage';
import { CohortUnsubscribePage } from '@/pages/assessment-engine/CohortUnsubscribePage';

// Education Pages
import { EducationPage } from '@/pages/education/EducationPage';
import { EducationModuleDetailPage } from '@/pages/education/EducationModuleDetailPage';

// Stakeholder Management Pages
import { StakeholderManagementPage } from '@/pages/stakeholders/StakeholderManagementPage';
import { CalendarPage } from '@/pages/calendar/CalendarPage';

// Business Performance Pages
import { DashboardOverviewPage } from '@/pages/business-performance/DashboardOverviewPage';
import { PlanHealthDetailPage } from '@/pages/business-performance/PlanHealthDetailPage';
import { ObjectivePerformancePage } from '@/pages/business-performance/ObjectivePerformancePage';
import { TargetVsActualPage } from '@/pages/business-performance/TargetVsActualPage';
import { HistoricalTrendsPage } from '@/pages/business-performance/HistoricalTrendsPage';
import { EnterActualsPage } from '@/pages/business-performance/EnterActualsPage';
import { KpiLoadsPage } from '@/pages/kpis/KpiLoadsPage';
import { KpiHistoryPage } from '@/pages/business-performance/KpiHistoryPage';

// Discussion Forum Pages
import { DiscussionForumPage } from '@/pages/discussions/DiscussionForumPage';

// Issue Tracking, Maintenance Mode, and Admin Notifications Pages
import { IssueManagementPage } from '@/pages/issues/IssueManagementPage';
import { IssueDetailPage } from '@/pages/issues/IssueDetailPage';
import { CreateIssuePage } from '@/pages/issues/CreateIssuePage';

// Action Management System (RAIL) Pages
import { ActionManagementPage } from '@/pages/actions/ActionManagementPage';
import { ActionDetailPage } from '@/pages/actions/ActionDetailPage';
import { MaintenanceModePage } from '@/pages/admin/MaintenanceModePage';
import { AdminNotificationManagementPage } from '@/pages/admin/AdminNotificationManagementPage';
import { SystemLogsPage } from '@/pages/admin/SystemLogsPage';
import { LogFileViewerPage } from '@/pages/admin/LogFileViewerPage';
import { ActivityDashboardPage } from '@/pages/admin/ActivityDashboardPage';
import { IssueCategoriesPage } from '@/pages/admin/IssueCategoriesPage';
import { RiskCategoriesPage } from '@/pages/admin/RiskCategoriesPage';
import { ContentManagementPage } from '@/pages/admin/ContentManagementPage';
import TenantHierarchiesPage from '@/pages/admin/TenantHierarchiesPage';
import AdminAIFreeUsagePage from '@/pages/admin/AdminAIFreeUsagePage';
import AdminBillingPage from '@/pages/admin/AdminBillingPage';
import { AdminLoginPage } from '@/pages/auth/AdminLoginPage';
import { MaintenanceScreen } from '@/components/maintenance/MaintenanceScreen';
import { useState, useEffect } from 'react';
import { maintenanceModeService } from '@/services/maintenanceModeService';

/** Maps legacy tenant config URLs to /tenant-configuration/:tabId, preserving query (e.g. Stripe return). */
function LegacyTenantConfigRedirect() {
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const tab = qs.get('card') === 'complete' ? 'billing-card' : 'roles';
  return <Navigate to={{ pathname: `/tenant-configuration/${tab}`, search }} replace />;
}

/** Old /business-performance page URLs → /performance. API paths are unchanged. */
function LegacyPerformanceRedirect() {
  const location = useLocation();
  const next = location.pathname.replace(/^\/business-performance/, '/performance');
  return <Navigate to={`${next}${location.search}${location.hash}`} replace />;
}

/**
 * Component to check maintenance mode and redirect users appropriately
 * Simple logic: If system is in unexpired maintenance mode and user is not platform admin,
 * redirect to maintenance page and show maintenance message.
 */
const MaintenanceCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [maintenanceActive, setMaintenanceActive] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        // Use the public maintenance status endpoint which checks both old and new systems
        const status = await maintenanceModeService.getStatus();
        
        // Check if maintenance is active and not expired
        if (status.is_active && status.scheduled_end_time) {
          const endTime = new Date(status.scheduled_end_time).getTime();
          const now = new Date().getTime();
          setMaintenanceActive(now < endTime);
        } else {
          setMaintenanceActive(status.is_active || false);
        }
      } catch (error) {
        console.error('Error checking maintenance status:', error);
        setMaintenanceActive(false);
      } finally {
        setChecking(false);
      }
    };

    checkMaintenance();
  }, [isAuthenticated, user]); // Re-check when user authentication state changes

  // Show loading while checking maintenance
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow admin login and maintenance pages always (case-insensitive check)
  const pathname = location.pathname.toLowerCase();
  if (pathname === '/admin/login' || pathname === '/maintenance' || pathname.includes('/admin/login')) {
    return <>{children}</>;
  }

  // If maintenance is active and not expired
  if (maintenanceActive) {
    // Check if user is platform admin - platform admins can operate normally
    const isPlatformAdmin = user?.role === 'Platform Administrator' || user?.role === 'PLATFORM_ADMIN';
    
    // Platform admins bypass maintenance and can operate normally
    if (isAuthenticated && isPlatformAdmin) {
      return <>{children}</>;
    }

    // Non-admin users (or not logged in) - redirect to maintenance page
    return <Navigate to="/maintenance" replace />;
  }

  // No maintenance active - allow normal flow
  return <>{children}</>;
};

// Activity tracking hook
import { useActivityTracking } from '@/hooks/useActivityTracking';

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Track page views and errors automatically (hook handles auth check internally)
  useActivityTracking();

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <MaintenanceCheck>
      <Routes>
        {/* Public routes */}
        <Route path="/ai/planning" element={<PublicPlanningPage />} />
        <Route path="/try" element={<Navigate to="/ai/planning" replace />} />
        {/* Why T-MOS merged into home; keep redirect for bookmarks and old links */}
        <Route path="/why-tmos" element={<Navigate to="/" replace />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/solutions" element={<SolutionsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/request-demo" element={<RequestDemoPage />} />
        <Route path="/tmos-vision" element={<Navigate to="/" replace />} />
        {/* Public landing: problem → solution → CTA → who → value → differentiation */}
        <Route path="/" element={<PublicLandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/assessment/take/:linkToken" element={<TakeAssessmentPage />} />
        <Route
          path="/assessment/take/:linkToken/results/:responseId"
          element={<AssessmentResultsPage />}
        />
        <Route path="/assessment/report/:shareToken" element={<LiveReportPage />} />
        <Route path="/assessment/unsubscribe/:token" element={<InviteUnsubscribePage />} />
        <Route path="/assessment/cohort-unsubscribe/:token" element={<CohortUnsubscribePage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <EnhancedRegisterPage />
      } />
      <Route path="/register-enhanced" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <EnhancedRegisterPage />
      } />
      <Route path="/confirm-email" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <EmailConfirmationPage />
      } />
      <Route path="/forgot-password" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
      } />
              <Route path="/reset-password" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />
        } />
        <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      
      {/* Admin login route (bypasses maintenance mode) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      
      {/* Maintenance mode screen (shown to non-admin users during maintenance) */}
      <Route path="/maintenance" element={<MaintenanceScreen />} />
      
      {/* Onboarding routes */}
      <Route path="/onboarding/individual" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <OnboardingWizard />
      } />
      <Route path="/onboarding/portfolio-companies" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <OnboardingWizard />
      } />
      <Route path="/onboarding/company-transformations" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <OnboardingWizard />
      } />
      <Route path="/onboarding/transformation-services" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <OnboardingWizard />
      } />
        
      {/* Protected routes - nested under /dashboard and other protected paths */}
      <Route element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        {/* Partner Routes */}
        <Route path="partner/received-invitations" element={
          <ProtectedRBACRoute>
            <ReceivedInvitationsPage />
          </ProtectedRBACRoute>
        } />

        <Route path="pe-portfolio" element={
          <ProtectedRBACRoute>
            <PePortfolioPage />
          </ProtectedRBACRoute>
        } />
        
        <Route path="/accept-invitation" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <AcceptInvitationPage />
        } />
        <Route path="/dashboard" element={
          <ProtectedRBACRoute>
            <DashboardPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Admin pages */}
        <Route path="/admin/dashboard" element={
          <ProtectedRBACRoute>
            <AdminDashboardPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Module pages - shows all screens for a module */}
        <Route path="/modules/*" element={
          <ProtectedRBACRoute>
            <ModulePage />
          </ProtectedRBACRoute>
        } />
        
        {/* Transformation routes */}
        {/* Performance Dashboard */}
        <Route path="performance" element={
          <ProtectedRBACRoute>
            <DashboardOverviewPage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/plans/:id" element={
          <ProtectedRBACRoute>
            <PlanHealthDetailPage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/objectives/:id" element={
          <ProtectedRBACRoute>
            <ObjectivePerformancePage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/target-vs-actual" element={
          <ProtectedRBACRoute>
            <TargetVsActualPage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/trends/:objectiveId" element={
          <ProtectedRBACRoute>
            <HistoricalTrendsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/enter-actuals" element={
          <ProtectedRBACRoute>
            <EnterActualsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/bulk-actuals" element={
          <ProtectedRBACRoute>
            <Navigate to="/kpis/loads" replace />
          </ProtectedRBACRoute>
        } />
        <Route path="performance/kpis/:id" element={
          <ProtectedRBACRoute>
            <KpiHistoryPage />
          </ProtectedRBACRoute>
        } />
        <Route path="business-performance/*" element={<LegacyPerformanceRedirect />} />
        <Route path="business-performance" element={<LegacyPerformanceRedirect />} />

        <Route path="transformations" element={
          <ProtectedRBACRoute>
            <TransformationsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="transformations/create" element={
          <ProtectedRBACRoute>
            <TransformationFormPage />
          </ProtectedRBACRoute>
        } />
        <Route path="transformations/:id" element={
          <ProtectedRBACRoute>
            <TransformationDetailPage />
          </ProtectedRBACRoute>
        } />
        <Route path="transformations/:id/edit" element={
          <ProtectedRBACRoute>
            <TransformationFormPage />
          </ProtectedRBACRoute>
        } />
        
        {/* KPI routes */}
        <Route path="kpis" element={
          <ProtectedRBACRoute>
            <KPIsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="kpis/loads" element={
          <ProtectedRBACRoute>
            <KpiLoadsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="kpis/create" element={
          <ProtectedRBACRoute>
            <KPIFormPage />
          </ProtectedRBACRoute>
        } />
        <Route path="kpis/:id" element={
          <ProtectedRBACRoute>
            <KPIDetailPage />
          </ProtectedRBACRoute>
        } />
        <Route path="kpis/:id/edit" element={
          <ProtectedRBACRoute>
            <KPIFormPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Tenant routes */}
        <Route path="tenants" element={
          <ProtectedRBACRoute>
            <TenantsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenants/create" element={
          <ProtectedRBACRoute>
            <TenantFormPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenants/:id" element={
          <ProtectedRBACRoute>
            <TenantDetailPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenants/:id/edit" element={
          <ProtectedRBACRoute>
            <TenantFormPage />
          </ProtectedRBACRoute>
        } />
        
        <Route path="process-models" element={
          <ProtectedRBACRoute>
            <ProcessModelsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="processes" element={
          <ProtectedRBACRoute>
            <ProcessModelsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="change-management" element={
          <ProtectedRBACRoute>
            <ChangeManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="risk" element={
          <ProtectedRBACRoute>
            <RiskManagementPage />
          </ProtectedRBACRoute>
        } />

        {/* Assessment Engine */}
        <Route path="assessment-engine/templates" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_TEMPLATES">
            <TemplateListPage />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/templates/:templateId" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_TEMPLATES">
            <TemplateBuilderPage />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/settings" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_SETTINGS">
            <EngineSettingsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/campaigns" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_CAMPAIGNS">
            <CampaignListPage />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/campaigns/new" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_CAMPAIGNS">
            <CampaignDeployWizard />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/campaigns/:campaignId/report" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_CAMPAIGNS">
            <AssessmentReportPage />
          </ProtectedRBACRoute>
        } />
        <Route path="assessment-engine/campaigns/:campaignId" element={
          <ProtectedRBACRoute screenCode="ASSESSMENT_ENGINE_CAMPAIGNS">
            <CampaignDetailHub />
          </ProtectedRBACRoute>
        } />
        
        {/* Resources & Knowledge Base routes */}
        <Route path="resources" element={
          <ProtectedRBACRoute>
            <ResourcesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="resources/:id" element={
          <ProtectedRBACRoute>
            <ResourceViewPage />
          </ProtectedRBACRoute>
        } />
        <Route path="resources/admin" element={
          <ProtectedRBACRoute>
            <ResourcesAdminPage />
          </ProtectedRBACRoute>
        } />
        
      {/* Benchmarking routes */}
      <Route path="benchmarking" element={
        <ProtectedRBACRoute>
          <BenchmarkingPage />
        </ProtectedRBACRoute>
      } />
      <Route path="admin/process-admin" element={
        <ProtectedRBACRoute>
          <BenchmarkProcessAdminPage />
        </ProtectedRBACRoute>
      } />
      <Route path="benchmarking/:id" element={
        <ProtectedRBACRoute>
          <BenchmarkingPage />
        </ProtectedRBACRoute>
      } />
      <Route path="benchmarking/:id/entries" element={
        <ProtectedRBACRoute>
          <BenchmarkEntriesPage />
        </ProtectedRBACRoute>
      } />
      <Route path="benchmarking/:id/propagation" element={
        <ProtectedRBACRoute>
          <HierarchyPropagationPage />
        </ProtectedRBACRoute>
      } />
        
        <Route path="data-import" element={
          <ProtectedRBACRoute>
            <DataImportPage />
          </ProtectedRBACRoute>
        } />
        <Route path="settings" element={
          <ProtectedRBACRoute>
            <SettingsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="settings/profile" element={
          <ProtectedRBACRoute>
            <UserProfilePage />
          </ProtectedRBACRoute>
        } />
        <Route path="settings/tenant-subscription-configuration" element={<LegacyTenantConfigRedirect />} />
        <Route path="tenant-subscription-configuration" element={<LegacyTenantConfigRedirect />} />
        <Route path="settings/tenant-configuration" element={<LegacyTenantConfigRedirect />} />
        <Route path="tenant-configuration/:tabId" element={
          <ProtectedRBACRoute>
            <TenantConfigurationPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenant-configuration" element={<Navigate to="/tenant-configuration/roles" replace />} />
        <Route path="reports" element={
          <ProtectedRBACRoute>
            <DashboardPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Navigation Test Route */}
        <Route path="navigation-test" element={
          <ProtectedRBACRoute>
            <NavigationTestPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Onboarding Route */}
        <Route path="onboarding" element={
          <ProtectedRBACRoute>
            <OnboardingWizard />
          </ProtectedRBACRoute>
        } />
        
        {/* Education Routes */}
        <Route path="education" element={
          <ProtectedRBACRoute>
            <EducationPage />
          </ProtectedRBACRoute>
        } />
        <Route path="education/:moduleId" element={
          <ProtectedRBACRoute>
            <EducationModuleDetailPage />
          </ProtectedRBACRoute>
        } />
        
        {/* New Hierarchical Navigation Routes */}
        {/* Platform Administration Routes */}
        <Route path="admin/provisioning-profiles" element={
          <ProtectedRBACRoute>
            <ProvisioningProfilesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/user-provisioning" element={
          <ProtectedRBACRoute>
            <UserProvisioningPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/platform-settings" element={
          <ProtectedRBACRoute screenCode="PLATFORM_SETTINGS">
            <PlatformSettingsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/ai/settings" element={
          <ProtectedRBACRoute screenCode="ADMIN_AI_SETTINGS">
            <AdminAIFreeUsagePage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/billing" element={
          <ProtectedRBACRoute screenCode="ADMIN_BILLING">
            <AdminBillingPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenant/ai/settings" element={
          <ProtectedRBACRoute screenCode="TENANT_AI_SETTINGS">
            <AISettingsPage scope="tenant" />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_HOME">
            <AIStudioHomePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/conversations" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_CONVERSATION">
            <AIStudioConversationPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/catalog" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_CATALOG">
            <AIStudioAgentMarketplacePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/agents" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_AGENTS">
            <AIStudioAgentCatalogPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/agents/:agentId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_AGENTS">
            <AIStudioAgentDefinitionPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/agents/:agentId/definition" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_AGENTS">
            <AIStudioAgentDefinitionRedirect />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/agents/:agentId/versions" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_AGENTS">
            <AIStudioAgentDetailPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/agents/:agentId/versions/:versionId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_AGENTS">
            <AIStudioVersionEditorPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/packs" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_PACKS">
            <AIStudioPackCatalogPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/packs/:packId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_PACKS">
            <AIStudioPackEditorPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioInstanceCatalogPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances/provision" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioProvisionInstancePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances/:instanceId/provision" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioProvisionInstancePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances/deploy" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioDeployInstancePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances/:instanceId/setup" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioInstanceSetupPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/instances/:instanceId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioInstanceEntryPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/bindings" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioBindingsRedirect />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/bindings/:bindingId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_INSTANCES">
            <AIStudioBindingsRedirect />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/history" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_HISTORY">
            <AIStudioHistoryHubPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/runs" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_RUNS">
            <AIStudioRunCatalogPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/runs/:runId" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_RUNS">
            <AIStudioRunWorkspacePage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai-studio/ontology" element={
          <ProtectedRBACRoute screenCode="AI_STUDIO_ONTOLOGY">
            <AIStudioOntologyBrowserPage />
          </ProtectedRBACRoute>
        } />
        {/* Redirect legacy routes to canonical /rbac/roles */}
        <Route path="admin/role-management" element={<Navigate to="/rbac/roles" replace />} />
        
        {/* System Maintenance Routes */}
        {/* Redirect legacy routes to canonical /admin/system-monitoring */}
        <Route path="admin/system-maintenance" element={<Navigate to="/admin/system-monitoring" replace />} />
        <Route path="admin/system-settings" element={<Navigate to="/admin/system-monitoring" replace />} />
        <Route path="admin/system-monitoring" element={
          <ProtectedRBACRoute>
            <SystemMonitoringPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/performance-tuning" element={
          <ProtectedRBACRoute>
            <PerformanceTuningPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/backup-recovery" element={
          <ProtectedRBACRoute>
            <BackupRecoveryPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/audit-logging" element={
          <ProtectedRBACRoute>
            <AuditLoggingPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Templates Routes - Separated by type */}
        <Route path="templates/manage" element={
          <ProtectedRBACRoute>
            <TemplateManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/hierarchy" element={
          <ProtectedRBACRoute>
            <HierarchyTemplatesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/roadmap" element={
          <ProtectedRBACRoute>
            <RoadmapTemplatesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/role" element={
          <ProtectedRBACRoute>
            <RoleTemplatesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/project-structure" element={
          <ProtectedRBACRoute>
            <ProjectStructureTemplatesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/templates" element={<Navigate to="/templates/manage" replace />} />
        <Route path="admin/template-masters" element={<Navigate to="/templates/manage" replace />} />
        <Route path="admin/policy-templates" element={<Navigate to="/templates/manage" replace />} />
        <Route path="admin/role-templates" element={<Navigate to="/templates/role" replace />} />
        <Route path="admin/transformation-templates" element={<Navigate to="/templates/manage" replace />} />
        <Route path="admin/process-templates" element={<Navigate to="/templates/manage" replace />} />
        
        {/* Template Marketplace Routes - Consolidated to /marketplace */}
        <Route path="templates/comprehensive-builder" element={
          <ProtectedRBACRoute screenCode="ROADMAP_TEMPLATE_COMPREHENSIVE_BUILDER">
            <PureHierarchicalTemplateBuilder />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/hierarchy/:templateId/edit" element={
          <ProtectedRBACRoute>
            <TemplateHierarchyEditorPage />
          </ProtectedRBACRoute>
        } />
        <Route path="templates/project-structure-builder" element={
          <ProtectedRBACRoute>
            <ProjectStructureTemplateBuilder />
          </ProtectedRBACRoute>
        } />
        <Route path="tenant/roadmaps/builder" element={
          <ProtectedRBACRoute>
            <PureHierarchicalTemplateBuilder />
          </ProtectedRBACRoute>
        } />
        
        {/* Navigation Management Routes */}
        <Route path="admin/navigation-management" element={
          <ProtectedRBACRoute>
            <NavigationManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/navigation/hierarchy" element={
          <ProtectedRBACRoute>
            <NavigationManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/navigation/hierarchy-old" element={
          <ProtectedRBACRoute>
            <NavigationHierarchyPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/navigation/modules" element={
          <ProtectedRBACRoute>
            <NavigationManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/navigation/screens" element={
          <ProtectedRBACRoute>
            <NavigationManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/navigation/nodes" element={
          <ProtectedRBACRoute>
            <NavigationManagementPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Access Management Routes */}
        <Route path="admin/custom-attributes" element={
          <ProtectedRBACRoute>
            <CustomAttributesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/actions" element={
          <ProtectedRBACRoute>
            <ActionsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/value-assignment" element={
          <ProtectedRBACRoute>
            <UserAttributeAssignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/policy-creation" element={
          <ProtectedRBACRoute>
            <PolicyCreatePage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/policy-assignment" element={
          <ProtectedRBACRoute>
            <PolicyManagementPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Business Management Routes */}
        <Route path="admin/partner-management" element={
          <ProtectedRBACRoute>
            <PartnerDirectoryPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/data-import" element={
          <ProtectedRBACRoute>
            <DataImportPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/data-connection" element={
          <ProtectedRBACRoute>
            <DataImportPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Legacy Administrative Routes */}
        <Route path="admin/abac" element={
          <ProtectedRBACRoute>
            <PolicyManagementPage />
          </ProtectedRBACRoute>
        } />
        {/* Redirect legacy /admin/rbac to canonical /rbac/roles */}
        <Route path="admin/rbac" element={<Navigate to="/rbac/roles" replace />} />
        <Route path="admin/system" element={
          <ProtectedRBACRoute>
            <SystemMonitoringPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Legacy Administrative Routes - Redirected to canonical routes */}
        <Route path="navigation-hierarchy" element={<Navigate to="/admin/navigation-management" replace />} />
        <Route path="audit-logging" element={<Navigate to="/admin/audit-logging" replace />} />
        <Route path="security-management" element={
          <ProtectedRBACRoute>
            <SecurityManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="backup-recovery" element={<Navigate to="/admin/backup-recovery" replace />} />
        <Route path="system-monitoring" element={<Navigate to="/admin/system-monitoring" replace />} />
        <Route path="performance-tuning" element={<Navigate to="/admin/performance-tuning" replace />} />
        <Route path="integration-management" element={
          <ProtectedRBACRoute>
            <IntegrationManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="api-management" element={
          <ProtectedRBACRoute>
            <ApiManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="license-management" element={
          <ProtectedRBACRoute>
            <LicenseManagementPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Master Data Management Routes */}
        
        {/* User & Access Management Routes */}
        <Route path="access-management" element={
          <ProtectedRBACRoute>
            <AccessManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="user-management" element={
          <ProtectedRBACRoute>
            <UserManagementPage />
          </ProtectedRBACRoute>
        } />
        {/* Redirect legacy routes to canonical /rbac/roles */}
        <Route path="users" element={<Navigate to="/user-management" replace />} />
        <Route path="role-management" element={<Navigate to="/rbac/roles" replace />} />
        <Route path="rbac/roles" element={
          <ProtectedRBACRoute>
            <RoleManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="rbac/roles/create" element={
          <ProtectedRBACRoute>
            <RoleCreatePage />
          </ProtectedRBACRoute>
        } />
        <Route path="rbac/roles/:role/edit" element={
          <ProtectedRBACRoute>
            <RoleEditPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/password-management" element={
          <ProtectedRBACRoute>
            <PasswordManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/user-approvals" element={
          <ProtectedRBACRoute>
            <UserApprovalPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/user-positions" element={
          <ProtectedRBACRoute>
            <UserPositionManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/position-templates" element={
          <ProtectedRBACRoute>
            <PositionTemplatesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/positions" element={
          <ProtectedRBACRoute>
            <TenantPositionsPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Transformation Management Routes */}
        
        {/* Process Modeling Routes */}
        
        {/* KPI & Performance Management Routes */}
        
        {/* Tenant Administration Routes */}
        
        {/* Partner Management Routes */}
        <Route path="partner/directory" element={
          <ProtectedRBACRoute>
            <PartnerDirectoryPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Roadmap Marketplace Routes - Detail page requires auth */}
        <Route path="marketplace" element={
          <ProtectedRBACRoute>
            <RoadmapMarketplacePage />
          </ProtectedRBACRoute>
        } />
        <Route path="marketplace/:id" element={
          <ProtectedRBACRoute>
            <RoadmapDetailPage />
          </ProtectedRBACRoute>
        } />
        {/* Marketplace create and manage routes removed - use /tenant/roadmaps/builder and /templates/manage instead */}
        
        {/* Tenant Planning — AI workspace; legacy tab URL redirects here */}
        <Route path="tenant/planning" element={
          <ProtectedRBACRoute screenCode="AI_PLANNING">
            <PlanningPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenant/roadmaps/planning" element={
          <ProtectedRBACRoute>
            <PlanningLegacyRedirect />
          </ProtectedRBACRoute>
        } />
        {/* Tenant Roadmaps — saved roadmaps list */}
        <Route path="tenant/roadmaps" element={
          <ProtectedRBACRoute>
            <RoadmapsPage />
          </ProtectedRBACRoute>
        } />
        <Route path="tenant/roadmaps/:id" element={
          <ProtectedRBACRoute>
            <RoadmapDetailPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Tenant Transformations - Individual project management */}
        <Route path="tenant/transformations/:id" element={
          <ProtectedRBACRoute>
            <TransformationDetailPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Data Connection — sources only (KPI catalog/targets/actuals live under /kpis and /business-performance) */}
        <Route path="data-connection" element={<Navigate to="/data-connection/sources" replace />} />
        <Route path="data-connection/sources" element={
          <ProtectedRBACRoute screenCode="DATA_SOURCE_MANAGER">
            <DataSourceManagerPage />
          </ProtectedRBACRoute>
        } />
        <Route path="data-connection/sources/new" element={
          <ProtectedRBACRoute screenCode="DATA_SOURCE_MANAGER">
            <DataSourceFormPage />
          </ProtectedRBACRoute>
        } />
        <Route path="data-connection/sources/:id" element={
          <ProtectedRBACRoute screenCode="DATA_SOURCE_MANAGER">
            <DataSourceFormPage />
          </ProtectedRBACRoute>
        } />
        <Route path="data-connection/sources/:id/edit" element={
          <ProtectedRBACRoute screenCode="DATA_SOURCE_MANAGER">
            <DataSourceFormPage />
          </ProtectedRBACRoute>
        } />
        {/* Legacy Data Connection KPI URLs → spine screens */}
        <Route path="data-connection/kpi-setup" element={<Navigate to="/kpis" replace />} />
        <Route path="data-connection/kpi-dashboard" element={<Navigate to="/performance" replace />} />
        <Route path="data-connection/kpi-mappings" element={<Navigate to="/kpis" replace />} />
        <Route path="data-connection/kpi-library" element={<Navigate to="/kpis" replace />} />
        <Route path="data-connection/imports" element={<Navigate to="/kpis/loads" replace />} />
        
        {/* CRM Routes */}
        <Route path="crm" element={
          <ProtectedRBACRoute screenCode="CRM_DASHBOARD">
            <UnifiedCRMPage />
          </ProtectedRBACRoute>
        } />
        <Route path="crm/:id" element={
          <ProtectedRBACRoute screenCode="CRM_DASHBOARD">
            <UnifiedCRMPage />
          </ProtectedRBACRoute>
        } />
        <Route path="outbound/today" element={<Navigate to="/crm/today" replace />} />
        <Route path="outbound/workflows" element={<Navigate to="/crm/workflows" replace />} />
        <Route path="outbound/import" element={<Navigate to="/crm/import" replace />} />
        
        {/* Stakeholder Management Routes - match /stakeholders and /stakeholders/create */}
        <Route path="stakeholders/*" element={
          <ProtectedRBACRoute>
            <StakeholderManagementPage />
          </ProtectedRBACRoute>
        } />

        <Route path="calendar" element={
          <ProtectedRBACRoute>
            <CalendarPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Discussion Forum Routes */}
        <Route path="discussions" element={
          <ProtectedRBACRoute>
            <DiscussionForumPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Alignment Management Routes */}
        <Route path="alignment" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/visions" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/objectives" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/strategies" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/projects" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/linking" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        <Route path="alignment/reports" element={
          <ProtectedRBACRoute>
            <AlignmentPage />
          </ProtectedRBACRoute>
        } />
        
        {/* Hierarchy Management Routes */}
        <Route path="hierarchies" element={
          <ProtectedRBACRoute>
            <HierarchiesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="business-hierarchy" element={
          <ProtectedRBACRoute>
            <BusinessHierarchyPage />
          </ProtectedRBACRoute>
        } />
        <Route path="functional-hierarchy" element={
          <ProtectedRBACRoute>
            <FunctionalHierarchyPage />
          </ProtectedRBACRoute>
        } />
        <Route path="geographic-hierarchy" element={
          <ProtectedRBACRoute>
            <GeographicHierarchyPage />
          </ProtectedRBACRoute>
        } />
        <Route path="hierarchy-template-management" element={
          <ProtectedRBACRoute>
            <TemplateManagementPage />
          </ProtectedRBACRoute>
        } />
        
        {/* AI-Powered Features Routes */}
        <Route path="ai" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai/roadmap-generator" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai/corporate-memory" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai/prompt-management" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai/settings" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
        <Route path="ai/model-usage" element={
          <ProtectedRBACRoute>
            <AIPage />
          </ProtectedRBACRoute>
        } />
                 {/* ABAC Management Routes */}
                     <Route path="abac/custom-attributes" element={
          <ProtectedRBACRoute>
            <CustomAttributesPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/user-attributes" element={
          <ProtectedRBACRoute>
            <UserAttributesPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/resource-attributes" element={
          <ProtectedRBACRoute>
            <ResourceAttributesPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/environment-attributes" element={
          <ProtectedRBACRoute>
            <EnvironmentAttributesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="abac/test-environment" element={
          <ProtectedRBACRoute>
            <TestEnvironmentPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/actions" element={
          <ProtectedRBACRoute>
            <ActionsPage />
          </ProtectedRBACRoute>
        } />
            
            {/* ABAC View & Assign Values Routes */}
            <Route path="abac/view-users" element={
          <ProtectedRBACRoute>
            <ViewUsersPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/view-resources" element={
          <ProtectedRBACRoute>
            <ViewResourcesPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/user-attribute-assignment" element={
          <ProtectedRBACRoute>
            <UserAttributeAssignmentPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/resource-attribute-assignment" element={
          <ProtectedRBACRoute>
            <ResourceAttributeAssignmentPage />
          </ProtectedRBACRoute>
        } />
            
            {/* ABAC Test Route */}
            <Route path="abac/test" element={
          <ProtectedRBACRoute>
            <TestUserAttributePage />
          </ProtectedRBACRoute>
        } />
            
            {/* ABAC Policy Management Routes */}
            <Route path="abac/policies" element={
          <ProtectedRBACRoute>
            <PolicyManagementPage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/policies/create" element={
          <ProtectedRBACRoute>
            <PolicyCreatePage />
          </ProtectedRBACRoute>
        } />
            <Route path="abac/policies/test" element={
          <ProtectedRBACRoute>
            <PolicyTestPage />
          </ProtectedRBACRoute>
        } />
            
            {/* Issue Tracking Routes */}
            <Route path="issues" element={
              <ProtectedRBACRoute screenCode="ISSUE_MANAGEMENT">
                <IssueManagementPage />
              </ProtectedRBACRoute>
            } />
            <Route path="issues/create" element={
              <ProtectedRBACRoute screenCode="ISSUE_MANAGEMENT">
                <CreateIssuePage />
              </ProtectedRBACRoute>
            } />
            <Route path="issues/:id" element={
              <ProtectedRBACRoute screenCode="ISSUE_MANAGEMENT">
                <IssueDetailPage />
              </ProtectedRBACRoute>
            } />
            
            {/* Action Management System (RAIL) Routes */}
            <Route path="actions" element={
              <ProtectedRBACRoute screenCode="ACTION_MANAGEMENT">
                <ActionManagementPage />
              </ProtectedRBACRoute>
            } />
            <Route path="actions/:id" element={
              <ProtectedRBACRoute screenCode="ACTION_MANAGEMENT">
                <ActionDetailPage />
              </ProtectedRBACRoute>
            } />
            
            {/* Platform Admin Routes */}
            <Route path="admin/maintenance" element={
              <ProtectedRBACRoute screenCode="MAINTENANCE_MODE">
                <MaintenanceModePage />
              </ProtectedRBACRoute>
            } />
            <Route path="admin/notifications" element={
              <ProtectedRBACRoute>
                <AdminNotificationManagementPage />
              </ProtectedRBACRoute>
            } />
            <Route path="admin/logs" element={
              <ProtectedRBACRoute>
                <SystemLogsPage />
              </ProtectedRBACRoute>
            } />
            <Route path="admin/log-files" element={
              <ProtectedRBACRoute>
                <LogFileViewerPage />
              </ProtectedRBACRoute>
            } />
            <Route path="admin/activity" element={
              <ProtectedRBACRoute>
                <ActivityDashboardPage />
              </ProtectedRBACRoute>
            } />
        <Route path="admin/issue-categories" element={
          <ProtectedRBACRoute>
            <IssueCategoriesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/risk-categories" element={
          <ProtectedRBACRoute>
            <RiskCategoriesPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/content-management" element={
          <ProtectedRBACRoute>
            <ContentManagementPage />
          </ProtectedRBACRoute>
        } />
        <Route path="admin/tenant-hierarchies" element={
          <ProtectedRBACRoute>
            <TenantHierarchiesPage />
          </ProtectedRBACRoute>
        } />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </MaintenanceCheck>
  );
};

export default App;
